import React, { useEffect, useState, useCallback } from 'react';
import { FiUser, FiUpload } from 'react-icons/fi';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';

import {
  useBlindComprehensivePacsSync,
  useComprehensivePacsSync,
} from '../../../helpers/api/pacs/pacs';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { VerticalStepper, Step } from './Stepper';
import { MobileStepper } from './MobileStepper';

import { PacsExaminationStep } from './steps/PacsExaminationStep';
import Step2_PatientAndPregnancy from '../NewCaseReverseUpload/steps/Step2_PatientAndPregnancy';

interface InstanceData {
  id: number;
  sopInstanceUID: string;
  sopClassUID: string;
  seriesInstanceUID: string;
  instanceNumber: string;
  transferSyntax: string;
  pixelSpacing: string;
  numberOfFrames: number;
  receivedAt: string;
  synced: boolean;
  syncAttemptedAt: string | null;
  syncError: string | null;
  filePath?: string;
  signedUrl?: string;
  signedUrlExpiry?: string;
}

interface StudyData {
  study_instance_uid: string;
  study_date: string;
  study_mode: string;
  is_locked: boolean;
  category?: string;
  serie?: Array<{
    created_at: string;
    updated_at: string | null;
    serie_instance_uid: string;
    study_instance_uid: string;
    type: string;
  }>;
  pregnancy?: {
    id: number;
    patient_id: string;
    estimated_due_date: string;
    last_menstrual_period: string;
    gestational_age_weeks: number;
    gestational_age_days: number;
    pregnancy_type: string;
    dating_method: string;
    fetal_presentation: string;
    planned_delivery_mode: string;
    pregnancy_status: string;
    closed_at?: string | null;
    outcome_date?: string | null;
    created_at: string;
    updated_at: string | null;
  };
  patient?: {
    id: string;
    patient_name: string;
    patient_last_name: string;
    birth_date: string;
  };
  user?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

export interface PacsStudy {
  studyInstanceUID: string;
  patientName: string;
  studyDate: string;
  studyTime: string;
  aeType: string;
  modality: string;
  numberOfInstances: number;
  lastMenstrualPeriod?: string;
  patientBirthDate?: string;
}

interface PacsStudyDetailsData {
  status: string;
  message: string;
  data: {
    study: StudyData | null;
    id: number;
    studyInstanceUID: string;
    patientId: string | null;
    patientName: string;
    patientBirthDate: string;
    studyDate: string;
    studyTime: string;
    studyDescription: string | null;
    modality: string;
    referringPhysician: string | null;
    accessionNumber: string | null;
    lastMenstrualPeriod: string | null;
    numberOfInstances: number;
    receivedAt: string;
    sourceAeTitle: string;
    aeType: string;
    fullySync: boolean;
    syncedInstanceCount: number;
    unsyncedInstanceCount: number;
    instances: InstanceData[];
  };
  datetime: string;
  status_code: number;
}

interface SyncError {
  response?: { data?: { detail?: string } };
  message?: string;
}

const stepsConfig: Step[] = [
  { id: 1, name: 'Patient & Pregnancy', icon: <FiUser size={16} /> },
  { id: 2, name: 'Study Details & Sync', icon: <FiUpload size={16} /> },
];

interface PacsSyncWizardProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPacsStudy: PacsStudy | null;
  pacsStudyDetails: PacsStudyDetailsData | null;
  onSyncSuccess?: () => void;
}

export const PacsSyncWizard = ({
  isOpen,
  onClose,
  selectedPacsStudy,
  pacsStudyDetails,
  onSyncSuccess,
}: PacsSyncWizardProps) => {
  const studyExists = !!pacsStudyDetails?.data?.study;

  const [currentStep, setCurrentStep] = useState(1);
  const [suggestedLMP, setSuggestedLMP] = useState<string | null>(null);
  
  const [wizardState, setWizardState] = useState<{
    patient: any | null;
    pregnancy: any | null;
    study: any | null;
    extractedPatients?: any[];
  }>({ patient: null, pregnancy: null, study: null, extractedPatients: [] });

  useEffect(() => {
    if (isOpen && pacsStudyDetails && selectedPacsStudy) {
      const study = pacsStudyDetails.data.study;

      // --- FIX START ---
      // Parse the patient name from the "FirstName^LastName" format
      // to create an object that the AddPatientForm component expects.
      const rawPatientName = selectedPacsStudy.patientName || '';
         const nameParts = rawPatientName.split(/[ \^-]+/)
                            // Filter out any empty strings that might result from multiple separators
                            .filter(part => part.length > 0);

      // As per your requirement: ADAM is first name, MAIMUNA is last name
      const firstName = nameParts[1] || '';
      const lastName = nameParts[0] || '';

      const extractedPatientFromPacs = {
        // Provide the separate fields AddPatientForm is looking for
        firstName: firstName,
        lastName: lastName,
        // Also provide a clean, combined name for display purposes if needed
        patientName: [firstName, lastName].filter(Boolean).join(' '),
        patientID: '', // PACS doesn't provide our internal DB ID
        patientBirthDate: selectedPacsStudy.patientBirthDate || '', // Expects YYYYMMDD string
      };
      // --- FIX END ---

      setCurrentStep(studyExists ? 2 : 1);

      setWizardState({
        patient: study?.patient || null,
        pregnancy: study?.pregnancy || null,
        study: null,
        // Use the newly parsed patient object
        extractedPatients: studyExists ? [] : [extractedPatientFromPacs],
      });

      let parsedLMP = null;
      if (selectedPacsStudy.lastMenstrualPeriod?.length === 8) {
        const lmpStr = selectedPacsStudy.lastMenstrualPeriod;
        parsedLMP = `${lmpStr.substring(0, 4)}-${lmpStr.substring(4, 6)}-${lmpStr.substring(6, 8)}`;
      }

      const finalLMP = study?.pregnancy?.last_menstrual_period || parsedLMP;
      setSuggestedLMP(finalLMP);
    }
  }, [isOpen, pacsStudyDetails, selectedPacsStudy, studyExists]);

  const onSyncComplete = () => {
    messageI18n.success('pacs.studySyncedSuccess');
    onClose();
    if (onSyncSuccess) onSyncSuccess();
  };

  const onSyncError = (error: SyncError) => {
    message.error(
      error?.response?.data?.detail ||
        error?.message ||
        tToast('pacs.studySyncFailed')
    );
  };

  const { mutate: comprehensiveSync, isPending: isSyncing } =
    useComprehensivePacsSync();
  const { mutate: blindComprehensiveSync, isPending: isBlindSyncing } =
    useBlindComprehensivePacsSync();
  const isSubmitting = isSyncing || isBlindSyncing;

  const handleNext = () =>
    currentStep < stepsConfig.length && setCurrentStep((p) => p + 1);
  const handleBack = () =>
    currentStep > 1 && !studyExists && setCurrentStep((p) => p - 1);

  const handleSubmit = () => {
    if (!wizardState.study || !selectedPacsStudy) return;

    const { examType, studyCategory, sop_instance_uids } = wizardState.study;
    
    const existingStudy = pacsStudyDetails?.data?.study;
    const existingCategory = existingStudy?.category;
    
    const existingObSerie = studyExists && examType === 'OB' && existingStudy?.serie
      ? existingStudy.serie.find(serie => serie.type === 'OB')
      : null;

    const syncData = {
      patient_id: wizardState.patient?.id,
      pregnancy_id: wizardState.pregnancy?.id
        ? typeof wizardState.pregnancy.id === 'string' 
          ? parseInt(wizardState.pregnancy.id, 10)
          : wizardState.pregnancy.id
        : undefined,
      study_category:
        examType === 'OB' || examType === 'Blind' 
          ? (studyExists 
              ? (existingCategory as 'T1' | 'T2' | 'T3') || studyCategory
              : studyCategory) 
          : null,
      type: examType,
      serie_instance_uid: existingObSerie?.serie_instance_uid,
      pacs_study_data: {
        id: pacsStudyDetails?.data?.id,
        studyInstanceUID: selectedPacsStudy.studyInstanceUID,
      },
      sop_instance_uids: sop_instance_uids,
    };

    const payload = {
      studyInstanceUid: selectedPacsStudy.studyInstanceUID,
      syncData,
    };
    
    if (examType === 'Blind') {
      blindComprehensiveSync(payload, {
        onSuccess: onSyncComplete,
        onError: onSyncError,
      });
    } else {
      comprehensiveSync(payload, {
        onSuccess: onSyncComplete,
        onError: onSyncError,
      });
    }
  };

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return wizardState.patient !== null && wizardState.pregnancy !== null;
      case 2:
        return !!wizardState.study;
      default:
        return false;
    }
  };

  const handleStudyComplete = useCallback(
    (study: any) => setWizardState((p) => ({ ...p, study })),
    []
  );

  if (!isOpen) return null;

  const steps = stepsConfig.map((step) => ({
    ...step,
    isComplete: studyExists && step.id < 2, 
  }));
  const currentStepInfo = steps.find((s) => s.id === currentStep);
  const nextStepInfo = steps.find((s) => s.id === currentStep + 1);
  const isFinalStep = currentStep === steps.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
    >
      <div className="flex flex-col lg:flex-row h-full">
        <div className="hidden h-fit lg:flex flex-col w-1/3 lg:w-1/4 bg-card-color-200 dark:bg-gray-900 p-8 border-r border-slate-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-title-page-color-100 dark:text-white mb-12">
            Sync Examination Case
          </h2>
          <VerticalStepper steps={steps} currentStep={currentStep} />
        </div>

        <div className="flex-1 flex flex-col bg-card-color-100 dark:bg-gray-800 overflow-y-auto">
          <div className="lg:hidden border-b border-slate-200 dark:border-gray-700">
            {currentStepInfo && (
              <MobileStepper
                currentStep={currentStep}
                totalSteps={steps.length}
                currentStepName={currentStepInfo.name}
                nextStepName={nextStepInfo?.name}
              />
            )}
          </div>

          <div className="flex-grow overflow-hidden bg-card-color-200 dark:bg-gray-900">
            {currentStep === 1 && (
              <Step2_PatientAndPregnancy
                wizardState={wizardState}
                setWizardState={setWizardState}
              />
            )}

            {currentStep === 2 && pacsStudyDetails && (
              <PacsExaminationStep
                pacsStudyDetails={pacsStudyDetails}
                studyExists={studyExists}
                suggestedLMP={suggestedLMP}
                pregnancyData={wizardState.pregnancy}
                onComplete={handleStudyComplete}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-x-4 px-6 py-4 border-t border-slate-200 dark:border-gray-700 bg-card-color-200 dark:bg-gray-800">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <div className="flex items-center gap-x-4">
              {currentStep > 1 && !studyExists && (
                <Button variant="secondary" onClick={handleBack}>
                  Back
                </Button>
              )}

              {!isFinalStep ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepComplete(currentStep)}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isStepComplete(currentStep) || isSubmitting}
                >
                  {isSubmitting ? 'Syncing...' : 'Complete Sync'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};